import { Injectable } from "@angular/core";

import { EnvironmentService } from "./environment.service";
import { Logger } from "../model/logger.model";

    /**
     * @description
     *  Servicio de log. Permite loguear en consola según la configuración del
     *  entorno de ejecución actual.
     */

@Injectable({ providedIn: "root" })
export class LoggingService {

    /**
     * @description
     *  El conjunto de nombres ya registrados. No pueden existir 2 loggers
     *  registrados bajo el mismo nombre en el mismo inyector.
     */
    private loggers: Map<string, Logger> = new Map<string, Logger>();

    constructor(private environmentService: EnvironmentService) {}

    /**
     * @description
     *  Construye un nuevo logger con el nombre especificado si dicho nombre
     *  nunca fue registrado anteriormente, o devuelve el logger previamente
     *  registrado bajo dicha clave si ya existía uno. Se garantiza que los
     *  nombres sean únicos manteniendo un registro de los loggers creados
     *  en la aplicación, lo que evita problemas al destruir una vista y
     *  recargarla nuevamente.
     *
     * @param name
     *  El nombre asociado al nuevo logger, o al logger existente.
     *
     * @returns
     *  La instancia del logger.
     *
     * @example
     *  private logger: Logger = this.loggingService.getLogger("UsersListComponent");
     *  ...
     *  constructor(private loggingService: LoggingService) {}
     *  ...
     *  public ngOnInit(): void {
     *      this.logger.fatal("A severe and big error.");
     *  }
     */
    public getLogger(name: string): Logger {
        const logger: Logger = this.loggers.get(name);
        if (logger !== undefined) {
            return logger;
        }
        else {
            const logger: Logger = new Logger(name, this.environmentService.loggingLevel);
            this.loggers.set(name, logger);
            return logger;
        }
    }

    /**
     * @description
     *  Elimina un logger registrado bajo cierto nombre. Luego de ejecutar
     *  este método, el nombre estará nuevamente disponible para registrar un
     *  nuevo logger. El método es idempotente, es decir, si se remueve un
     *  logger 2 o más veces bajo la misma clave, o si el logger no existía,
     *  las sucesivas llamadas devolverán 'undefined' como logger previamente
     *  registrado.
     *
     * @param name
     *  El nombre del logger a eliminar (si existe).
     *
     * @returns
     *  El logger previamente registrado bajo el nombre especificado, o
     *  'undefined' si no había uno.
     *
     * @example
     *  private logger: Logger = this.loggingService.getLogger("FakeLogger");
     *  ...
     *  constructor(private loggingService: LoggingService) {}
     *  ...
     *  public ngOnDestroy(): void {
     *      const logger = this.loggingService.removeLogger("FakeLogger");
     *      logger.info("A last message before disappear...");
     *  }
     */
    public removeLogger(name: string): Logger {
        const logger: Logger = this.loggers.get(name);
        if (logger !== undefined) {
            this.loggers.delete(name);
        }
        return logger;
    }
}
